import React from "react";

/**
 * DDS-001 · Chapter 10 · §2.2 · The Maritime Shield with Anchor
 * ────────────────────────────────────────────────────────────
 * White maritime shield containing a white Admiralty (stockless) anchor
 * on a deep red circular background. Fills only, no outlines. Shield
 * occupies ~64% of the circle diameter.
 *
 * Two variants:
 *   variant="primary"  — 24 px and larger. Shield + Anchor + Circle.
 *   variant="compact"  — below 24 px, favicon, OS notifications.
 *                        Shield alone with a subtle anchor keyline;
 *                        the anchor becomes visual noise at 16 px.
 *
 * Screen-reader label is fixed to "Safety and Emergency" per §2.2.
 * Never "Red button", never "Icon", never "Emergency".
 *
 * Governed by /app/docs/product-office/DDS-001-CHAPTER-10-EMERGENCY.md.
 * Do not modify the geometry without a Chapter 10 amendment.
 */
export const SafetyMark = React.forwardRef(function SafetyMark(
  { size = 24, variant, className, title = "Safety and Emergency", ...rest },
  ref,
) {
  const resolved = variant || (size < 24 ? "compact" : "primary");
  const isPrimary = resolved === "primary";
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="img"
      aria-label={title}
      className={className}
      focusable="false"
      {...rest}
    >
      <title>{title}</title>
      {/* Red disc background */}
      <circle cx="24" cy="24" r="24" fill="var(--cc-safety-red, #A31621)" />
      {/* Maritime shield — rounded top, subtle pointed base. ~64% of
          the disc diameter → shield spans 30.72 units, centred on 24,24. */}
      <path
        d="M24 9
           C 32 9, 38 10.5, 39 12
           L 39 24
           C 39 33, 32.5 38, 24 40
           C 15.5 38, 9 33, 9 24
           L 9 12
           C 10 10.5, 16 9, 24 9 Z"
        fill="var(--cc-safety-white, #FFFFFF)"
      />
      {isPrimary ? (
        // Primary Mark · Admiralty anchor (symmetric, no rope).
        // Rendered in red on the white shield.
        <g fill="var(--cc-safety-red, #A31621)">
          {/* Ring at the top */}
          <circle cx="24" cy="15" r="1.6" fill="none" stroke="var(--cc-safety-red, #A31621)" strokeWidth="1.2" />
          {/* Shank */}
          <rect x="23.1" y="17" width="1.8" height="14" rx="0.6" />
          {/* Stock (crossbar) */}
          <rect x="19" y="18.6" width="10" height="1.4" rx="0.6" />
          {/* Curved arms + flukes */}
          <path
            d="M24 30
               C 20 32, 17.5 32, 15.5 30.3
               L 17 29
               C 18.5 30.1, 20.5 30.2, 22 29.4 Z"
          />
          <path
            d="M24 30
               C 28 32, 30.5 32, 32.5 30.3
               L 31 29
               C 29.5 30.1, 27.5 30.2, 26 29.4 Z"
          />
        </g>
      ) : (
        // Compact Mark · Shield only with a subtle anchor keyline.
        // Anchor is intentionally reduced to a single vertical shank so
        // the mark stays legible at 16 px.
        <g stroke="var(--cc-safety-red, #A31621)" strokeWidth="1.4" strokeLinecap="round">
          <line x1="24" y1="17" x2="24" y2="31" />
          <line x1="20" y1="19.4" x2="28" y2="19.4" />
        </g>
      )}
    </svg>
  );
});

export default SafetyMark;
